import {useContext, useEffect} from 'react';
import {useDevCycleClient} from '@devcycle/react-client-sdk';
import cx from 'classnames';
import Image from 'next/image';

import {Footer, FullscreenLoading, Header} from '#component/partial';
import {UserStateContext} from '#component/provider';
import {EventType, PageViewEvent, useAnalytics} from '#component/provider/AnalyticsProvider';
import {MAIN_CONTENT_NODE_ID} from '#constant/app';

import {ScreenProps} from './Screen.types';

import {contentStyles, rootStyles, sidePanelStyles} from './Screen.css';

export const Screen = ({
  children,
  className,
  contentContainerClassName,
  hero,
  loading = false,
  renderContent = true,
  hideHeader = false,
  pageName,
  pageData,
  headerProps = {variant: 'light'},
  sidePanelImageUrl,
  withDefaultSizing = true,
  ...otherProps
}: ScreenProps) => {
  const {track} = useAnalytics();
  const devCycleClient = useDevCycleClient();
  const {authenticatedUser} = useContext(UserStateContext);
  const userData = authenticatedUser
    ? {
        initials: authenticatedUser?.userProfile?.initials,
        avatar: authenticatedUser?.avatar?.url,
        hasActiveSubscription: authenticatedUser?.subscription?.hasActiveSubscription,
        hasMembership: authenticatedUser?.subscription?.hasMembership,
      }
    : undefined;

  const stringifiedPageData = JSON.stringify(pageData);

  useEffect(() => {
    if (authenticatedUser) {
      devCycleClient.identifyUser({
        isAnonymous: false,
        user_id: authenticatedUser.id,
        ...(authenticatedUser.userProfile?.email
          ? {email: authenticatedUser.userProfile.email}
          : {}),
      });
    }
  }, [devCycleClient, authenticatedUser]);

  useEffect(() => {
    if (pageName && !loading) {
      track<PageViewEvent>({
        evtName: EventType.PageView,
        pageName,
        ...(stringifiedPageData ? {data: JSON.parse(stringifiedPageData)} : {}),
      });
    }
  }, [loading, track, pageName, stringifiedPageData]);

  if (loading) {
    return <FullscreenLoading />;
  }

  const hasSidePanel = !!sidePanelImageUrl && sidePanelImageUrl.trim().length > 0;

  return renderContent ? (
    <>
      <div className={cx(rootStyles({hideHeader}), className)} {...otherProps}>
        {!hideHeader && <Header userData={userData} {...headerProps} />}

        {hero}

        <main
          className={cx(
            contentStyles({withSidePanel: hasSidePanel, withDefaultSizing}),
            contentContainerClassName,
          )}
          id={MAIN_CONTENT_NODE_ID}
          tabIndex={-1}>
          {children}
        </main>

        {hasSidePanel && (
          <div className={cx(sidePanelStyles({hasHeader: !hideHeader}))}>
            {sidePanelImageUrl && (
              <Image
                src={sidePanelImageUrl}
                alt="Side Panel"
                layout="fill"
                objectFit="cover"
                priority={true}
              />
            )}
          </div>
        )}
      </div>
      <Footer
        isAuthenticated={!!userData}
        hasActiveSubscription={!!userData?.hasActiveSubscription}
        hasMembership={!!userData?.hasMembership}
      />
    </>
  ) : null;
};
